// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ChangeFulfillmentMethodModal from "@bg/App/Components/ChangeFulfillmentMethodModal/ChangeFulfillmentMethodModal";
import DeliveryInventoryMessage from "@bg/App/Components/OnePageCheckout/DeliveryInventoryMessage";
import xSetUseBillingAddress from "@bg/App/Handlers/OnePageCheckout/SetUseBillingAddress";
import xSetUseOneTimeAddress from "@bg/App/Handlers/OnePageCheckout/SetUseOneTimeAddress";
import { CustomAppState } from "@bg/App/Store/Reducers";
import OnePageCheckoutBillingAddress from "@bg/App/Widgets/OnePageCheckout/OnePageCheckoutBillingAddress";
import OnePageCheckoutPickupAddress from "@bg/App/Widgets/OnePageCheckout/OnePageCheckoutPickupAddress";
import OnePageCheckoutShippingAddress from "@bg/App/Widgets/OnePageCheckout/OnePageCheckoutShippingAddress";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import setStorePickupOverlayIsOpen from "@insite/client-framework/Store/Components/StorePickupOverlay/Handlers/SetStorePickupOverlayIsOpen";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import setIsBillingAddressTheSameAsShipping from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/SetIsBillingAddressTheSameAsShipping";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { BillingAddressFormStyles } from "@insite/content-library/Widgets/OnePageCheckout/BillingAddressForm";
import { BillToSelectorModalStyles } from "@insite/content-library/Widgets/OnePageCheckout/BillToSelectorModal";
import { ShippingAddressFormStyles } from "@insite/content-library/Widgets/OnePageCheckout/ShippingAddressForm";
import { ShipToSelectorModalStyles } from "@insite/content-library/Widgets/OnePageCheckout/ShipToSelectorModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const CHECKOUT_SHIPPING_FORM_ID = "CHECKOUT_SHIPPING_FORM_ID";
export const CHECKOUT_BILLING_FORM_ID = "CHECKOUT_BILLING_FORM_ID";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { value: cart } = cartState;
    const shipTosDataView = cart
        ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
        : undefined;
    const session = getSession(state);
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;
    const pickUpWarehouse = cart?.defaultWarehouse || session.pickUpWarehouse;

    let newAddress;
    if (cart && cart.cartLines && shipTosDataView && shipTosDataView.value) {
        const shipTos = shipTosDataView.value;
        newAddress = shipTos!.find(s => s.isNew && !s.oneTimeAddress);
    }

    return {
        isBillingAddressTheSameAsShipping: state.pages.onePageCheckout.isBillingAddressTheSameAsShipping,
        cart,
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        currentUserIsGuest: getCurrentUserIsGuest(state),
        countries: getCurrentCountries(state),
        shipTosDataView,
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
        fulfillmentMethod,
        pickUpWarehouse,
        newAddress,
    };
};

const mapDispatchToProps = {
    loadShipTos,
    loadShipTo,
    loadBillTo,
    setIsBillingAddressTheSameAsShipping,
    setAddressBookOverlayIsOpen,
    setStorePickupOverlayIsOpen,
    // XNG-Changes
    xSetUseOneTimeAddress,
    xSetUseBillingAddress,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OnePageCheckoutShippingSectionStyles {
    loadingWrapper?: InjectableCss;
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    titleEditGridItem?: GridItemProps;
    titleEditLink?: LinkPresentationProps;
    pickUpGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    shipToSelectorModal?: ShipToSelectorModalStyles;
    billToSelectorModal?: BillToSelectorModalStyles;
    shipToGridItem?: GridItemProps;
    shippingAddressForm?: ShippingAddressFormStyles;
    addressInfoDisplay?: AddressInfoDisplayStyles;
    useShippingAddressGridItem?: GridItemProps;
    useShippingAddressCheckbox?: CheckboxPresentationProps;
    billToGridItem?: GridItemProps;
    billingAddressForm?: BillingAddressFormStyles;
    saveButtonGridItem?: GridItemProps;
    saveButton?: ButtonPresentationProps;
    /**
     * XNG-Changes
     */
    changeFulfillmentLink?: LinkPresentationProps;
    addressContainer?: InjectableCss;
}

export const onePageCheckoutShippingSectionStyles: OnePageCheckoutShippingSectionStyles = {
    loadingWrapper: {
        css: css`
            display: flex;
            height: 450px;
            justify-content: center;
            align-items: center;
        `,
    },
    container: { gap: 10 },
    titleGridItem: { width: 10 },
    titleText: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    titleEditGridItem: {
        width: 2,
        css: css`
            justify-content: end;
            align-items: center;
        `,
    },
    pickUpGridItem: {
        width: 12,
        css: css`
            padding-bottom: 24px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
            padding: 15px 5px 5px 5px;
        `,
    },
    heading: { weight: 600 },
    shipToGridItem: { width: 12 },
    useShippingAddressGridItem: {
        width: 12,
        css: css`
            padding: 20px 5px;
        `,
    },
    billToGridItem: { width: 12 },
    saveButtonGridItem: { width: 12 },
    saveButton: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = onePageCheckoutShippingSectionStyles;

const OnePageCheckoutShippingSection = ({
    cart,
    shipToState,
    billToState,
    countries,
    shipTosDataView,
    shipToAddressFields,
    fulfillmentMethod,
    loadShipTos,
    loadShipTo,
    loadBillTo,
}: Props) => {
    /**
     * XNG-Changes
     */
    const [isChangeFulfillmentModalOpen, setIsChangeFulfillmentModalOpen] = React.useState(false);

    const shipTo = shipToState.value;
    const billTo = billToState.value;

    useEffect(() => {
        if (cart && shipTosDataView && !shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos({ billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] });
        }
    });

    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && cart && cart.billToId && cart.shipToId) {
            loadShipTo({ billToId: cart.billToId, shipToId: cart.shipToId });
        }
    }, [shipToState]);

    useEffect(() => {
        if (!billToState.value && !billToState.isLoading && cart && cart.billToId) {
            loadBillTo({ billToId: cart.billToId });
        }
    }, [billToState]);

    if (!cart) {
        return null;
    }

    if (!countries || !shipTo || !billTo) {
        return null;
    }

    const handleOpenChangeFulfillmentMethod = () => {
        setIsChangeFulfillmentModalOpen(true);
    };

    const handleCloseChangeFulfillmentMethod = () => {
        setIsChangeFulfillmentModalOpen(false);
    };

    /**
     * XNG-Changes
     * Move LoadingOverlay to OnePageCheckoutPage
     */
    return (
        <>
            <StyledWrapper {...styles.container} data-test-selector="shippingAndBillingSection">
                <StyledWrapper {...styles.titleGridItem}>
                    <Typography {...styles.titleText}>{translate("Addresses")}</Typography>
                    {cart?.type === "Quote" && cart?.status === "QuoteProposed" && (
                        <Link
                            {...styles.changeFulfillmentLink}
                            onClick={handleOpenChangeFulfillmentMethod}
                            data-test-selector="checkoutReviewAndSubmit_changeShipping"
                        >
                            {translate(
                                "Change Fulfillment Method to {0}",
                                fulfillmentMethod === FulfillmentMethod.PickUp
                                    ? translate("Delivery")
                                    : translate("Pick Up"),
                            )}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.addressContainer}>
                    <OnePageCheckoutBillingAddress cart={cart} />
                    {fulfillmentMethod === FulfillmentMethod.Ship && shipToAddressFields && (
                        <OnePageCheckoutShippingAddress cart={cart} />
                    )}
                    {fulfillmentMethod === FulfillmentMethod.PickUp && (
                        <StyledWrapper>
                            <OnePageCheckoutPickupAddress cart={cart} />
                            <DeliveryInventoryMessage cart={cart} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <ChangeFulfillmentMethodModal
                modalIsOpen={isChangeFulfillmentModalOpen}
                onModalClose={handleCloseChangeFulfillmentMethod}
                fulfillmentMethod={fulfillmentMethod}
                quoteID={cart?.id}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutShippingSection),
    definition: {
        displayName: "Shipping Section",
        group: "One Page Checkout",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
